import React, { useMemo, useState, useEffect } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Image,
  Platform,
} from 'react-native';
import type { ImageStyle } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { ModuleHeader } from '@cubemeet/ui';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../../store';
import { createTravel } from '@cubemeet/core/store/slices/travelsSlice';
import { useTravelI18n } from './useTravelI18n';
import { MODULE_COLORS } from '@cubemeet/core/theme';
import { useCountriesDataWeb } from './useCountriesData';
import CurrencyPicker from './components/CurrencyPicker';
import { SUPPORTED_CURRENCIES } from '@cubemeet/core/services/currencyService';
import PaymentModePicker from './components/PaymentModePicker';
import VisibilityPicker from './components/VisibilityPicker';
import { getTravelBackgroundAsset, listAvailableTravelBackgrounds } from '@cubemeet/ui';
import {
  DEFAULT_MIN_PARTICIPANTS,
  DEFAULT_MAX_PARTICIPANTS,
  type CreateTravelFormData,
  type BackgroundImage,
  validateCreateTravelForm,
} from '@cubemeet/core/modules/travel';
import { useGlobalToast } from '../../contexts/ToastContext';

interface CreateTravelProps {
  onBack: () => void;
  onSubmit?: (data: CreateTravelFormData) => void;
  fullWidth?: boolean; // when true, render full-bleed (no rounded shell) — used for pages not using app shell
}

const CreateTravel: React.FC<CreateTravelProps> = ({ onBack, onSubmit, fullWidth = false }) => {
  const { t } = useTravelI18n();
  const backgroundOptions = useMemo(() => listAvailableTravelBackgrounds(), []);

  // 基本信息状态
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [startDate, setStartDate] = useState<Date>(new Date());
  // Default end date to tomorrow to avoid initial invalid range
  const tomorrow = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); return d;
  }, []);
  const [endDate, setEndDate] = useState<Date>(tomorrow);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // extra fields from mobile
  const [currency, setCurrency] = useState('CNY');
  const [paymentMode, setPaymentMode] = useState('actual_average');
  const [visibility, setVisibility] = useState<'public' | 'friends_only' | 'private'>('friends_only');
  const [backgroundId, setBackgroundId] = useState<string | null>(null);
  const [customBackgroundUrl, setCustomBackgroundUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  const [budgetPerPerson, setBudgetPerPerson] = useState('');

  const dispatch = useDispatch<AppDispatch>();
  const authState = useSelector((state: any) => (state as any)?.auth ?? {});
  const userId = authState?.user?.id as string | undefined;
  const { showSuccess, showError, showInfo } = useGlobalToast();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const selectedBackground = useMemo<BackgroundImage | null>(() => {
    if (customBackgroundUrl) {
      return {
        id: 'custom',
        url: { uri: customBackgroundUrl },
        title: 'Custom Background',
        relativePath: customBackgroundUrl,
      };
    }
    if (!backgroundId) return null;
    return {
      id: backgroundId,
      url: getTravelBackgroundAsset(backgroundId),
      title: getBackgroundKeyName(backgroundId),
      relativePath: backgroundId,
    };
  }, [backgroundId, customBackgroundUrl]);

  // 格式化日期为 YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 解析日期字符串为 Date 对象
  const parseDate = (dateStr: string): Date => {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? new Date() : date;
  };

  // 从资源 key 中提取短名称，用于 translation e.g. 'assets/images/beach.webp' -> 'beach'
  function getBackgroundKeyName(key: string) {
    if (!key) return '';
    const parts = key.split('/');
    const filename = parts[parts.length - 1] || key;
    return filename.replace(/\.[^.]+$/, '');
  }

  // Safe translate helper: return a fallback if the translator returns the key or is missing
  const tt = (key: string, fallback: string) => {
    try {
      const v = (t as any)(key);
      // if translator returns the key itself (common when missing), use fallback
      if (!v || v === key) return fallback;
      return v;
    } catch (err) {
      return fallback;
    }
  };

  const handleBackgroundUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      showError(tt('createTravel.uploadInvalidType', '请上传图片文件'));
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      showError(tt('createTravel.uploadTooLarge', '图片大小不能超过5MB'));
      return;
    }

    // Create local preview URL
    const previewUrl = URL.createObjectURL(file);
    setCustomBackgroundUrl(previewUrl);
    setPendingFile(file);
    setBackgroundId(null); // Clear preset selection
    setShowBackgroundModal(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 使用 web countries hook (translate via travel i18n)
  const { countries, getCountryByCode } = useCountriesDataWeb(t as any);

  // 表单提交
  const handleSubmit = async () => {
    const formData: CreateTravelFormData = {
      projectName: projectName.trim(),
      projectDescription: projectDescription.trim(),
      startDate,
      endDate,
      minParticipants: String(DEFAULT_MIN_PARTICIPANTS),
      maxParticipants: String(DEFAULT_MAX_PARTICIPANTS),
      visibility,
      paymentMode: paymentMode as CreateTravelFormData['paymentMode'],
      budgetPerPerson: budgetPerPerson.trim(),
      selectedPreferences: [],
      selectedCountry: selectedCountry.trim(),
      selectedCity: selectedCity.trim(),
      selectedCurrency: currency,
      selectedBackground,
    };

    // 验证表单
    const errors = validateCreateTravelForm(formData, t as any);
    if (errors.length > 0) {
      // message may be an i18n key; use tt helper to fall back to Chinese
      const rawMsg = errors[0]?.message || 'createTravel.validation.missingFields';
      const message = tt(rawMsg, '请填写所有必填字段');
      // non-blocking toast
      try { showError(message); } catch (e) { /* fallback */ }
      return;
    }

    setIsSubmitting(true);
    try {
      let finalBackgroundUrl = formData.selectedBackground?.relativePath;

      // Handle pending upload if exists
      if (pendingFile) {
        try {
          showInfo(tt('createTravel.uploading', '正在上传封面...'));
          const uploadFormData = new FormData();
          uploadFormData.append('file', pendingFile);
          if (userId) {
            uploadFormData.append('userId', userId);
            uploadFormData.append('folder', `user/${userId}/temp`);
          } else {
            uploadFormData.append('folder', 'temp/cover');
          }

          const uploadUrl = (window as any).__CARD_IMAGE_UPLOAD__ || 'https://card-image-upload.cubemeet.app';
          const response = await fetch(uploadUrl, {
            method: 'POST',
            body: uploadFormData,
            mode: 'cors',
          });

          if (!response.ok) {
            const text = await response.text();
            throw new Error(`Upload failed: ${response.status} ${text}`);
          }

          const data = await response.json();
          const url = data.url || data.secure_url || (data.data && data.data.url);
          
          if (url) {
            finalBackgroundUrl = url;
          } else {
            throw new Error('No url in upload response');
          }
        } catch (uploadErr) {
          console.error('Background upload failed:', uploadErr);
          showError(tt('createTravel.uploadFailed', '封面上传失败，请重试'));
          setIsSubmitting(false);
          return;
        }
      }

      if (onSubmit) {
        // Update background url in formData if uploaded
        const finalFormData = { ...formData };
        if (finalBackgroundUrl && finalFormData.selectedBackground) {
          finalFormData.selectedBackground = {
            ...finalFormData.selectedBackground,
            relativePath: finalBackgroundUrl,
            url: { uri: finalBackgroundUrl }
          };
        }
        await onSubmit(finalFormData);
      } else {
        // If no onSubmit provided (common on web route), attempt to dispatch the Redux thunk
        if (!userId) {
          try { showError(tt('createTravel.web.loginRequired', '请先登录或使用移动端创建完整拼游。')); } catch (e) { /* fallback */ }
          setIsSubmitting(false);
          return;
        }

        const travelData: any = {
          name: formData.projectName,
          description: formData.projectDescription || undefined,
          location: formData.selectedCity ? `${formData.selectedCountry}-${formData.selectedCity}` : undefined,
          min_participants: parseInt(formData.minParticipants || String(DEFAULT_MIN_PARTICIPANTS)) || DEFAULT_MIN_PARTICIPANTS,
          max_participants: parseInt(formData.maxParticipants || String(DEFAULT_MAX_PARTICIPANTS)) || DEFAULT_MAX_PARTICIPANTS,
          budget_per_person: formData.budgetPerPerson ? parseFloat(formData.budgetPerPerson) : undefined,
          start_date: formData.startDate ? formData.startDate.toISOString() : undefined,
          end_date: formData.endDate ? formData.endDate.toISOString() : undefined,
          payment_mode: formData.paymentMode || undefined,
          visibility: formData.visibility || undefined,
          background_image_url: finalBackgroundUrl || undefined,
          status: 'active',
          currency: formData.selectedCurrency || 'CNY',
        };

        const resultAction: any = await dispatch(createTravel({ travelData, userId } as any));
        if (createTravel.fulfilled.match(resultAction)) {
          // success
        } else {
          const msg = resultAction.error?.message || '创建失败';
          throw new Error(msg);
        }
      }

      try { showSuccess(tt('createTravel.success', '旅行创建成功！完整功能请使用移动端。')); } catch (e) { /* fallback */ }
      onBack();
    } catch (error: any) {
      console.error('创建旅行失败:', error);
      const msg = error?.message || (error && String(error)) || tt('createTravel.genericError', '创建失败，请重试');
      try { showError(`创建失败：${msg}`); } catch (e) { /* fallback */ }
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerStyle = [
    styles.container,
    fullWidth && Platform.OS === 'web' ? styles.containerFullWidth : null,
  ];

  // use in-app date picker modal for all viewports by default

  // Mobile date picker modal state
  const [mobileDatePickerVisible, setMobileDatePickerVisible] = useState(false);
  const [mobilePickerTarget, setMobilePickerTarget] = useState<'start' | 'end' | null>(null);
  const [mobilePickerMonth, setMobilePickerMonth] = useState<{ year: number; month: number }>(() => ({ year: startDate.getFullYear(), month: startDate.getMonth() }));

  const openMobileDatePicker = (target: 'start' | 'end') => {
    setMobilePickerTarget(target);
    const refDate = target === 'start' ? startDate : endDate;
    setMobilePickerMonth({ year: refDate.getFullYear(), month: refDate.getMonth() });
    setMobileDatePickerVisible(true);
  };

  const closeMobileDatePicker = () => {
    setMobileDatePickerVisible(false);
    setMobilePickerTarget(null);
  };

  // generate calendar month matrix (weeks x days)
  const getMonthMatrix = (year: number, month: number) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startWeekday = first.getDay(); // 0-6 Sun-Sat
    const daysInMonth = last.getDate();
    const weeks: Array<Array<number | null>> = [];
    let week: Array<number | null> = Array(startWeekday).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
      week.push(d);
      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      weeks.push(week);
    }
    return weeks;
  };

  const selectMobileDate = (day: number) => {
    if (!mobilePickerTarget) return;
    const { year, month } = mobilePickerMonth;
    const selected = new Date(year, month, day);
    if (mobilePickerTarget === 'start') {
      setStartDate(selected);
      // ensure endDate not before start
      if (endDate < selected) setEndDate(new Date(selected.getTime() + 24 * 3600 * 1000));
    } else {
      setEndDate(selected);
      if (startDate > selected) setStartDate(new Date(selected.getTime() - 24 * 3600 * 1000));
    }
    closeMobileDatePicker();
  };

  const isSameDay = (a: Date, b: Date) => {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };



  return (
    <View style={containerStyle}>
      {Platform.OS === 'web' && (
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleBackgroundUpload}
        />
      )}
      <ModuleHeader
        moduleType="travel"
        title={t('createTravel.title')}
        subtitle={t('createTravel.header.subtitle')}
        showMiniCube
        topInset={0}
        showBackButton
        onBack={onBack}
      />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.form}>
          {/* 拼游名称 */}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>
              {t('createTravel.projectName')} <Text style={styles.required}>*</Text>
            </Text>
            <TextInput
              style={styles.input}
              value={projectName}
              onChangeText={setProjectName}
              placeholder={t('createTravel.projectNamePlaceholder')}
              placeholderTextColor="#9CA3AF"
              maxLength={20}
            />
            <Text style={styles.charCount}>{projectName.length}/20</Text>
          </View>

          {/* background picker removed from here per request; will be placed at bottom later */}

          {/* 拼游描述 */}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>
              {t('createTravel.projectDescription')} <Text style={styles.required}>*</Text>
            </Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={projectDescription}
              onChangeText={setProjectDescription}
              placeholder={t('createTravel.projectDescriptionPlaceholder')}
              placeholderTextColor="#9CA3AF"
              multiline
              numberOfLines={4}
              maxLength={200}
            />
            <Text style={styles.charCount}>{projectDescription.length}/200</Text>
          </View>

          {/* 旅行日期 */}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>
              {t('createTravel.dateRange')} <Text style={styles.required}>*</Text>
            </Text>
            <View style={styles.dateRow}>
              <View style={[styles.dateField, { marginRight: 16 }]}>
                <Text style={styles.dateLabel}>{t('createTravel.startDate')}</Text>
                <TouchableOpacity onPress={() => openMobileDatePicker('start')} activeOpacity={0.8} style={{ width: '100%', padding: 12, paddingRight: 20, boxSizing: 'border-box', borderRadius: 12, border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' as any }}>
                  <Text style={{ fontSize: 15, color: '#111827' }}>{formatDate(startDate)}</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.dateField}>
                <Text style={styles.dateLabel}>{t('createTravel.endDate')}</Text>
                <TouchableOpacity onPress={() => openMobileDatePicker('end')} activeOpacity={0.8} style={{ width: '100%', padding: 12, paddingRight: 20, boxSizing: 'border-box', borderRadius: 12, border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF' as any }}>
                  <Text style={{ fontSize: 15, color: '#111827' }}>{formatDate(endDate)}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>

          {/* 目的地 */}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>
              {t('createTravel.destination')} <Text style={styles.required}>*</Text>
            </Text>
            <Text style={styles.subLabel}>{t('createTravel.selectCountry')}</Text>
            <TouchableOpacity style={styles.countrySelector} onPress={() => setShowCountryModal(true)}>
              <View style={styles.countrySelectorContent}>
                {selectedCountry ? (
                  <Text style={styles.countrySelectedText}>{getCountryByCode(selectedCountry)?.flag + ' ' + getCountryByCode(selectedCountry)?.name}</Text>
                ) : (
                  <Text style={styles.placeholderText}>{t('createTravel.selectCountry')}</Text>
                )}
                <Text style={styles.caret}>▾</Text>
              </View>
            </TouchableOpacity>
            {selectedCountry && (
              <TouchableOpacity style={styles.citySelector} onPress={() => setShowCityModal(true)}>
                <View style={styles.citySelectorContent}>
                  {selectedCity ? (
                    <Text style={styles.cityName}>{selectedCity}</Text>
                  ) : (
                    <Text style={styles.placeholderText}>{t('createTravel.selectCity')}</Text>
                  )}
                  <Text style={styles.caret}>▾</Text>
                </View>
              </TouchableOpacity>
            )}
          </View>

          {/* 支付模式 */}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>{t('createTravel.paymentMode')}</Text>
            <PaymentModePicker
              value={paymentMode}
              onChange={setPaymentMode}
              t={t}
              options={[
                { id: 'actual_average', labelKey: 'createTravel.actualAverage', descKey: 'createTravel.actualAverageDesc' },
                { id: 'actual_share', labelKey: 'createTravel.actualShare', descKey: 'createTravel.actualShareDesc' },
              ]}
            />
          </View>

          {/* 结算货币 */}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>{tt('createTravel.settlementCurrency', '结算货币')}</Text>
            <CurrencyPicker value={currency} onChange={setCurrency} />
          </View>

          {/* 每人预算 */}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>{t('createTravel.budgetPerPerson')}</Text>
            <View style={styles.budgetRow}>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type="number"
                  value={budgetPerPerson}
                  onChange={(e) => setBudgetPerPerson(e.target.value)}
                  placeholder={t('createTravel.budgetPlaceholder')}
                  style={{ width: '100%', padding: '12px 80px 12px 16px', borderRadius: 12, border: '1px solid #E5E7EB', fontSize: 15, boxSizing: 'border-box' }}
                />
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#6B7280', fontSize: 14 }}>/人</span>
                <span style={{ position: 'absolute', right: 44, top: '50%', transform: 'translateY(-50%)', color: MODULE_COLORS.travel, fontWeight: 700 }}>{SUPPORTED_CURRENCIES.find(c => c.code === currency)?.symbol || currency}</span>
              </div>
            </View>
            <Text style={styles.charCount}>{t('createTravel.budgetHint')}</Text>
          </View>

          {/* 项目可见性 */}
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>{t('createTravel.projectVisibility')}</Text>
            <Text style={styles.subLabel}>{t('createTravel.visibilitySubtitle')}</Text>
            <VisibilityPicker
              value={visibility}
              onChange={(v) => setVisibility(v as any)}
              options={[
                { id: 'friends_only', labelKey: 'createTravel.friendsVisible', descKey: 'createTravel.friendsVisibleDesc', icon: 'people' },
                { id: 'private', labelKey: 'createTravel.privateProject', descKey: 'createTravel.privateProjectDesc', icon: 'lock' },
              ]}
              t={t as any}
            />
          </View>

            {/* 10. 背景图片选择（放在可见性下面 / 页面底部附近） */}
            <View style={styles.fieldGroup}>
              <Text style={styles.label}>{t('createTravel.backgroundImage') || t('createTravel.backgroundImages.title') || '背景图片'}</Text>
              <TouchableOpacity style={styles.backgroundSelector} onPress={() => setShowBackgroundModal(true)}>
                {selectedBackground ? (
                  <View style={styles.selectedBackground}>
                    <Image
                      source={(selectedBackground.url as any)}
                      style={styles.backgroundPreview as ImageStyle}
                      resizeMode="cover"
                    />
                    <View style={styles.backgroundTitleOverlay}>
                      <Text style={styles.backgroundTitle}>
                        {selectedBackground.id === 'custom' 
                          ? (t('createTravel.customBackground') || '自定义背景')
                          : (t(`createTravel.backgroundImages.${getBackgroundKeyName(selectedBackground.id)}`) || '')}
                      </Text>
                    </View>
                  </View>
                ) : (
                  <View style={styles.backgroundPlaceholder}>
                    {Platform.OS === 'web' ? (
                      // Use Material Icons ligature on web for reliable rendering
                      // (apps/web includes the Material Icons stylesheet)
                      // @ts-ignore - allow raw span in RN web
                      <span className="material-icons" style={{ fontSize: 36, color: '#ccc' }}>add_a_photo</span>
                    ) : (
                      <Icon name="add-a-photo" size={36} color="#ccc" />
                    )}
                    <Text style={styles.backgroundPlaceholderText}>{t('createTravel.selectBackgroundPlaceholder') || '点击选择背景图片'}</Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>

          {/* 提交按钮 */}
          <TouchableOpacity
            style={[styles.submitButton, isSubmitting && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={isSubmitting}
            activeOpacity={0.8}
          >
            {isSubmitting ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.submitButtonText}>{tt('createTravel.submit', '创建拼游')}</Text>
            )}
          </TouchableOpacity>

          {/* webNotice intentionally removed on web to match mobile behavior */}
        </View>
      </ScrollView>
      {/* 国家选择 Modal */}
      {showCountryModal && (() => {
        const inner = (
          <View style={[styles.modalOverlay, { position: 'fixed' as any }]}> 
            <View style={styles.modalContent}>
              <View style={styles.modalHeaderRow}>
                <Text style={styles.modalTitle}>{t('createTravel.selectCountry')}</Text>
                <TouchableOpacity onPress={() => setShowCountryModal(false)}>
                  <Icon name="close" size={20} color="#666" />
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.countryList}>
                {countries.map((country) => (
                  <TouchableOpacity
                    key={country.code}
                    style={[styles.countryItem, selectedCountry === country.code && styles.countryItemSelected]}
                    onPress={() => { setSelectedCountry(country.code); setSelectedCity(''); setShowCountryModal(false); }}
                  >
                    {/* Prefer CDN SVG flag on web; fall back to emoji text if image fails */}
                    {country.flagUrl ? (
                      <img
                        src={country.flagUrl}
                        alt={country.code}
                        onError={(e: any) => { e.currentTarget.style.display = 'none'; const next = e.currentTarget.nextElementSibling; if (next) next.style.display = 'inline'; }}
                        style={{ width: 28, height: 20, verticalAlign: 'middle', borderRadius: 4, objectFit: 'cover', marginRight: 12 }}
                      />
                    ) : null}
                    <Text style={[styles.countryItemName, selectedCountry === country.code && styles.countryItemNameSelected]}>
                      <span style={{ display: country.flagUrl ? 'none' : 'inline' }}>{country.flag}</span>
                      {country.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        );
        try {
          // Mount to document.body on web so modal is viewport-centered and above long page content
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const { createPortal } = require('react-dom');
          const g: any = (globalThis as any) || {};
          if (g && g.document && g.document.body) return createPortal(inner, g.document.body);
        } catch (e) {
          // fallback to inline render
        }
        return inner;
      })()}

      {/* 城市选择 Modal */}
      {showCityModal && selectedCountry && (() => {
        const inner = (
          <View style={[styles.modalOverlay, { position: 'fixed' as any }]}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeaderRow}>
                <Text style={styles.modalTitle}>{t('createTravel.selectCity')} - {getCountryByCode(selectedCountry)?.name}</Text>
                <TouchableOpacity onPress={() => setShowCityModal(false)}>
                  <Icon name="close" size={20} color="#666" />
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.cityList}>
                {getCountryByCode(selectedCountry)?.cities.map((city) => (
                  <TouchableOpacity
                    key={city}
                    style={[styles.cityItem, selectedCity === city && styles.cityItemSelected]}
                    onPress={() => { setSelectedCity(city); setShowCityModal(false); }}
                  >
                    <Text style={[styles.cityItemName, selectedCity === city && styles.cityItemNameSelected]}>{city}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        );
        try {
          const { createPortal } = require('react-dom');
          const g: any = (globalThis as any) || {};
          if (g && g.document && g.document.body) return createPortal(inner, g.document.body);
        } catch (e) {}
        return inner;
      })()}

      {/* 背景选择 Modal (React Native) */}
      {showBackgroundModal && (() => {
        const inner = (
          <View style={[styles.modalOverlay, { position: 'fixed' as any }]}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeaderRow}>
                <Text style={styles.modalTitle}>{t('createTravel.selectBackground')}</Text>
                <TouchableOpacity onPress={() => setShowBackgroundModal(false)}>
                  <Icon name="close" size={20} color="#666" />
                </TouchableOpacity>
              </View>

              <ScrollView contentContainerStyle={styles.backgroundGrid}>
                <TouchableOpacity
                  style={[styles.backgroundOption, styles.uploadOption]}
                  onPress={() => {
                    if (Platform.OS === 'web') {
                      fileInputRef.current?.click();
                    } else {
                      showError(tt('createTravel.mobileUploadNotSupported', '移动端暂不支持上传'));
                    }
                  }}
                >
                  <View style={styles.uploadPlaceholder}>
                    {Platform.OS === 'web' ? (
                      // @ts-ignore
                      <span className="material-icons" style={{ fontSize: 32, color: '#666' }}>cloud_upload</span>
                    ) : (
                      <Icon name="cloud-upload" size={32} color="#666" />
                    )}
                    <Text style={styles.uploadText}>{tt('createTravel.upload', '上传图片')}</Text>
                  </View>
                </TouchableOpacity>

                {backgroundOptions.map((item) => (
                  <TouchableOpacity
                    key={item.key}
                    style={[styles.backgroundOption, backgroundId === item.key && styles.backgroundOptionSelected]}
                    onPress={() => {
                      setBackgroundId(item.key);
                      setShowBackgroundModal(false);
                    }}
                  >
                    <Image source={(item.source as any)} style={styles.backgroundImage as ImageStyle} resizeMode="cover" />
                    <Text style={styles.backgroundOptionTitle}>{t(`createTravel.backgroundImages.${getBackgroundKeyName(item.key)}`) || ''}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        );
        try {
          const { createPortal } = require('react-dom');
          const g: any = (globalThis as any) || {};
          if (g && g.document && g.document.body) return createPortal(inner, g.document.body);
        } catch (e) {}
        return inner;
      })()}
      {/* Mobile in-app date picker modal for narrow screens */}
      {mobileDatePickerVisible && (() => {
        const inner = (
          <View style={[styles.modalOverlay, { position: 'fixed' as any }]} onStartShouldSetResponder={() => true}>
            <View style={[styles.modalContent, { maxWidth: 360, width: '94%', paddingHorizontal: 16 }]}>
              <View style={styles.modalHeaderRow}>
                <Text style={styles.modalTitle}>{mobilePickerTarget === 'start' ? t('createTravel.startDate') : t('createTravel.endDate')}</Text>
                <TouchableOpacity onPress={closeMobileDatePicker}>
                  <Icon name="close" size={20} color="#666" />
                </TouchableOpacity>
              </View>

              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <TouchableOpacity onPress={() => setMobilePickerMonth(m => { const d = new Date(m.year, m.month - 1, 1); return { year: d.getFullYear(), month: d.getMonth() }; })}>
                  <Text style={{ fontSize: 22 }}>‹</Text>
                </TouchableOpacity>
                <Text style={{ fontSize: 15, fontWeight: '600' }}>{mobilePickerMonth.year} - {String(mobilePickerMonth.month + 1).padStart(2, '0')}</Text>
                <TouchableOpacity onPress={() => setMobilePickerMonth(m => { const d = new Date(m.year, m.month + 1, 1); return { year: d.getFullYear(), month: d.getMonth() }; })}>
                  <Text style={{ fontSize: 22 }}>›</Text>
                </TouchableOpacity>
              </View>

              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d) => (
                  <Text key={d} style={{ width: '14%', textAlign: 'center', color: '#6B7280', fontSize: 12 }}>{d}</Text>
                ))}
              </View>

              {/* Weeks */}
              {getMonthMatrix(mobilePickerMonth.year, mobilePickerMonth.month).map((week, wi) => (
                <View key={wi} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                  {week.map((day, di) => (
                    day ? (
                      <TouchableOpacity
                        key={di}
                        onPress={() => selectMobileDate(day)}
                        activeOpacity={0.8}
                        style={{ width: '14%', aspectRatio: 1, alignItems: 'center', justifyContent: 'center', borderRadius: 8, backgroundColor: isSameDay(new Date(mobilePickerMonth.year, mobilePickerMonth.month, day), mobilePickerTarget === 'start' ? startDate : endDate) ? MODULE_COLORS.travel : 'transparent' }}
                      >
                        <Text style={{ color: isSameDay(new Date(mobilePickerMonth.year, mobilePickerMonth.month, day), mobilePickerTarget === 'start' ? startDate : endDate) ? '#fff' : '#111827' }}>{day}</Text>
                      </TouchableOpacity>
                    ) : (
                      <View key={di} style={{ width: '14%', height: 0 }} />
                    )
                  ))}
                </View>
              ))}

              <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 12 }}>
                <TouchableOpacity onPress={closeMobileDatePicker} style={{ paddingHorizontal: 12, paddingVertical: 8 }}>
                  <Text style={{ color: '#6B7280' }}>{t('createTravel.cancel') || '取消'}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        );
        try {
          // render portal on web for proper stacking
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const { createPortal } = require('react-dom');
          const g: any = (globalThis as any) || {};
          if (g && g.document && g.document.body) return createPortal(inner, g.document.body);
        } catch (e) {
          // fallback inline
        }
        return inner;
      })()}
      {/* Bottom nav is provided by AppLayout when this route is wrapped by the app shell. */}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        maxWidth: 600,
        width: '100%',
        alignSelf: 'center',
        borderRadius: 12,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      },
    }),
  },
  containerFullWidth: {
    // full-bleed: remove rounded corners and let header/content span full width on web
    borderRadius: 0,
    width: '100%',
    alignSelf: 'stretch',
    backgroundColor: '#FFFFFF',
    ...Platform.select({
      web: {
        maxWidth: '100%',
        boxShadow: 'none',
      },
    }),
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    paddingBottom: 120,
  },
  form: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  fieldGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  subLabel: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 8,
  },
  required: {
    color: MODULE_COLORS.travel,
  },
  input: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
    color: '#111827',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  charCount: {
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'right',
    marginTop: 4,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateField: {
    flex: 1,
  },
  dateLabel: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 8,
  },
  budgetRow: {
    marginTop: 8,
    marginBottom: 8,
  },
  orDivider: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    marginVertical: 12,
  },
  countrySelector: {
    marginTop: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  countrySelectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  countrySelectedText: {
    fontSize: 15,
    color: '#111827',
  },
  placeholderText: {
    fontSize: 15,
    color: '#9CA3AF',
  },
  caret: {
    fontSize: 20,
    color: '#6B7280',
    marginLeft: 8,
  },
  submitButton: {
    ...Platform.select({
      web: {
        backgroundColor: MODULE_COLORS.travel,
        borderRadius: 16,
        paddingVertical: 16,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 32,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      },
      default: {
        backgroundColor: MODULE_COLORS.travel,
        borderRadius: 16,
        paddingVertical: 16,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 32,
        shadowColor: MODULE_COLORS.travel,
        shadowOpacity: 0.3,
        shadowOffset: { width: 0, height: 4 },
        shadowRadius: 8,
        elevation: 4,
      },
    }),
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  webNotice: {
    fontSize: 13,
    color: '#9CA3AF',
    textAlign: 'center',
    marginTop: 16,
    lineHeight: 20,
  },
  modalHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  modalOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    width: '90%',
    maxWidth: 480,
    alignSelf: 'center',
    maxHeight: '80%',
    backgroundColor: '#fff',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  backgroundPreviewCard: {
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
  },
  backgroundSelector: {
    marginTop: 8,
  },
  selectedBackground: {
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  backgroundPreview: {
    width: '100%',
    height: 160,
    borderRadius: 12,
  },
  backgroundTitleOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
  backgroundTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  backgroundPlaceholder: {
    height: 160,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backgroundPlaceholderText: {
    color: '#9CA3AF',
    marginTop: 8,
  },
  backgroundGrid: {
    paddingTop: 8,
    paddingBottom: 24,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  backgroundOption: {
    width: '48%',
    marginBottom: 16,
    borderRadius: 12,
    overflow: 'hidden',
    alignItems: 'center',
  },
  backgroundOptionSelected: {
    borderWidth: 2,
    borderColor: MODULE_COLORS.travel,
  },
  backgroundImage: {
    width: '100%',
    height: 120,
    borderRadius: 12,
  },
  backgroundOptionTitle: {
    marginTop: 8,
    color: '#6B7280',
  },
  uploadOption: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderStyle: 'dashed',
    backgroundColor: '#F9FAFB',
  },
  uploadPlaceholder: {
    width: '100%',
    height: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadText: {
    marginTop: 8,
    color: '#6B7280',
    fontSize: 14,
  },
  countryList: {
    maxHeight: 400,
  },
  countryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  countryItemSelected: {
    backgroundColor: '#fff7ed',
  },
  countryFlag: { fontSize: 20, marginRight: 12 },
  countryItemName: { fontSize: 16, color: '#333' },
  countryItemNameSelected: { color: '#b45309', fontWeight: '600' },
  cityList: { maxHeight: 400 },
  cityItem: { paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f0f0f0' },
  cityItemSelected: { backgroundColor: '#fff7ed' },
  cityItemName: { fontSize: 16, color: '#333' },
  cityItemNameSelected: { color: '#b45309', fontWeight: '600' },
  citySelector: {
    marginTop: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  citySelectorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cityName: {
    fontSize: 15,
    color: '#111827',
  },
});

export default CreateTravel;